import React from 'react';
import AnimatedSection from './AnimatedSection';
import { ClockIcon } from './Icons';

const services = [
  {
    id: 'entspannung',
    title: 'Entspannungsmassage',
    description: 'Sanfte Techniken zur tiefen Entspannung von Körper und Geist. Ideal zum Stressabbau.',
    duration: '60 / 90 Min',
    image: '/img/relaxation-massage.jpg',
    price: 'ab €60'
  },
  {
    id: 'tiefengewebe',
    title: 'Tiefengewebsmassage',
    description: 'Intensive Behandlung für tiefe Muskelverspannungen und chronische Beschwerden.',
    duration: '60 / 90 Min',
    image: '/img/deep-tissue-massage.jpg',
    price: 'ab €60'
  },
  {
    id: 'lymph',
    title: 'Lymphmassage',
    description: 'Fördert die Lymphzirkulation und unterstützt die natürliche Entgiftung des Körpers.',
    duration: '60 / 90 Min',
    image: '/img/lymphatic-massage.jpg',
    price: 'ab €60'
  },
  {
    id: 'aroma',
    title: 'Aromaöl-Ritual',
    description: 'Luxuriöse Ganzkörpermassage mit ausgewählten ätherischen Ölen für Sinne und Seele.',
    duration: '60 / 90 Min',
    image: '/img/aroma-oil-therapy.jpg',
    price: 'nach Anfrage'
  },
  {
    id: 'sport',
    title: 'Sportmassage',
    description: 'Spezielle Techniken für Sportler zur Regeneration und Leistungssteigerung.',
    duration: '60 / 90 Min',
    image: '/img/sports-massage.jpg',
    price: 'ab €60'
  },
  {
    id: 'nacken',
    title: 'Nacken- & Rückenfokus',
    description: 'Gezielte Behandlung für Nacken, Schultern und Rücken bei Verspannungen.',
    duration: '60 / 90 Min',
    image: '/img/neck-back-focus.jpg',
    price: 'ab €60'
  }
];

const ServiceCard = ({ service, index }) => {
  return (
    <AnimatedSection delay={index * 100} className="h-full">
      <div
        data-testid={`service-card-${service.id}`}
        className="group relative h-full bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500"
      >
        {/* Image */}
        <div className="relative h-48 overflow-hidden">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/60 to-transparent" />
          
          {/* Price badge */}
          <div className="absolute top-4 right-4 bg-brand-gold text-brand-dark px-4 py-1.5 rounded-full font-work text-sm font-semibold">
            {service.price}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <h3 className="font-marcellus text-xl text-brand-dark mb-3 group-hover:text-brand-gold transition-colors duration-300">
            {service.title}
          </h3>

          <p className="font-work text-sm text-brand-dark/60 mb-4 leading-relaxed">
            {service.description}
          </p>

          {/* Duration */}
          <div className="flex items-center gap-2 text-brand-dark/50 mb-5">
            <ClockIcon className="w-4 h-4" />
            <span className="font-roboto text-sm">{service.duration}</span>
          </div>

          {/* CTA */}
          <a
            href="#buchung"
            data-testid={`service-book-${service.id}`}
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#buchung')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-brand-dark text-white font-work text-sm font-medium rounded-full group-hover:bg-brand-gold transition-all duration-300"
          >
            Termin buchen
            <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>

        {/* Hover accent line */}
        <div className="absolute bottom-0 left-0 w-full h-1 bg-brand-gold transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
      </div>
    </AnimatedSection>
  );
};

const Services = () => {
  return (
    <section
      id="leistungen"
      data-testid="services-section"
      className="py-24 md:py-32 bg-brand-light relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-gold/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-gold/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <AnimatedSection className="text-center mb-16">
          <span className="inline-flex items-center gap-2 font-work text-sm text-brand-gold uppercase tracking-widest mb-4">
            <span className="w-8 h-px bg-brand-gold" />
            Meine Angebote
            <span className="w-8 h-px bg-brand-gold" />
          </span>
          <h2 className="font-marcellus text-4xl md:text-5xl lg:text-6xl text-brand-dark mb-6">
            Leistungen
          </h2>
          <p className="font-work text-brand-dark/60 max-w-2xl mx-auto text-lg">
            Entdecken Sie meine Auswahl an Premium Massagen, individuell abgestimmt auf Ihre Bedürfnisse.
          </p>
          <p className="font-work text-brand-dark/60 max-w-2xl mx-auto text-lg mt-2">
            Alle Behandlungen finden bei Ihnen zu Hause statt.
          </p>
        </AnimatedSection>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} />
          ))}
        </div>

        {/* Bottom CTA */}
        <AnimatedSection className="text-center mt-16">
          <p className="font-work text-brand-dark/60 mb-4">
            Nicht sicher welche Behandlung? 
          </p>
          <a
            href="#kontakt"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#kontakt')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 text-brand-gold hover:text-brand-dark font-work font-medium transition-colors"
          >
            Lassen Sie sich beraten
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default Services;
